import { useAuth0 } from '@auth0/auth0-react';
import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import {
  useAccountInfoSuspense,
  useSubscriptionSuspense,
} from 'src/api/queries';
import { EditEmailForm } from 'src/apps/account/app/components/EditEmailForm';
import { EditMultiFactorAuth } from 'src/apps/account/app/components/EditMultiFactorAuth';
import { EditNameForm } from 'src/apps/account/app/components/EditNameForm';
import { EditPassword } from 'src/apps/account/app/components/EditPassword';
import { LinkedAccounts } from 'src/apps/account/app/components/LinkedAccounts';
import {
  Button,
  Divider,
  FaqSection,
  NotificationBox,
  Sheet,
} from 'src/components';
import { AppPage } from 'src/components/AppPage';
import { ChangePaymentIntervalBanners } from 'src/components/ChangePaymentIntervalBanners';
import { IS_DEVELOPMENT } from 'src/config';
import { FAQ } from 'src/constants/FAQ';
import { NoUserError } from 'src/utils/noUserError';
import { URLS } from 'src/utils/urls/urls';
import { userHelpers } from 'src/utils/userHelpers';

export const Account = () => {
  const { user } = useAuth0();
  const { t } = useLingui();
  const subscriptions = useSubscriptionSuspense();
  const accountInfo = useAccountInfoSuspense();

  if (!user) {
    throw new NoUserError('No user found when loading account page');
  }

  const isOrgMember = userHelpers.isOrganizationMember(user);
  const isDomainClaimed = userHelpers.isDomainClaimedByConnection(user);
  const isSocial = userHelpers.isSocialUser(user);
  const identities = accountInfo.data?.identities ?? [];
  const canEditPassword = userHelpers.canEditPassword(user, identities);
  const isLinkedToSAML = userHelpers.hasSAMLIdentity(identities);

  return (
    <AppPage.Container>
      <ChangePaymentIntervalBanners
        className="m-auto mt-64 mb-56 max-w-l2xl px-16"
        componentLocation={ComponentLocations.NOTIFICATIONS.ACCOUNT_PAGE}
        subscription={subscriptions.data}
      />

      <AppPage.Header
        className="max-w-l2xl"
        title={t`Account`}
        subtitle={t`Manage your payment methods, personal contact details, and view your
          account and payment history.`}
      ></AppPage.Header>
      <AppPage.Content className="max-w-l2xl">
        <Sheet className="gap-24 lm:gap-32">
          <div className="flex flex-col gap-24">
            <Typography variant="heading-subtle-2xl" as="h2">
              <Trans>Contact details</Trans>
            </Typography>

            <EditNameForm />
            <EditEmailForm />

            {isOrgMember && (
              <NotificationBox
                variant="info"
                muted
                title={t`This account is managed by an external identity provider.
                    Please contact your IT admin to manage your account.`}
              />
            )}

            {isSocial && isDomainClaimed && (
              <NotificationBox variant="info" title={t`Change email address`}>
                <Typography variant="body-md">
                  <Trans>
                    You can&apos;t change your email using social login. Contact
                    your IT admin.
                  </Trans>
                </Typography>
              </NotificationBox>
            )}
          </div>

          <Divider />

          <div className="flex flex-col gap-24">
            <Typography variant="heading-subtle-2xl" as="h2">
              <Trans>Subscription and payment</Trans>
            </Typography>

            <div className="flex flex-col justify-between gap-24 lm:flex-row">
              <Typography variant="body-md">
                <Trans>Manage your subscription and payment details.</Trans>
              </Typography>

              <Button
                as="a"
                variant="tertiary"
                to={URLS.SUBSCRIPTION}
                size="medium"
                className="w-full self-start whitespace-nowrap ls:w-fit"
              >
                <span>
                  <Trans>Go to subscription</Trans>
                </span>{' '}
                <ArrowRight />
              </Button>
            </div>
          </div>

          {!isLinkedToSAML && (
            <>
              <Divider />

              <div data-cy="security-card" className="flex flex-col gap-24">
                <Typography variant="heading-subtle-2xl" as="h2">
                  <Trans>Security</Trans>
                </Typography>

                {canEditPassword && <EditPassword />}

                <EditMultiFactorAuth />
              </div>
            </>
          )}

          {!isOrgMember && IS_DEVELOPMENT && (
            <>
              <Divider />

              <div className="flex flex-col gap-24">
                <Typography variant="heading-subtle-2xl" as="h2">
                  <Trans>Enterprise single sign-on</Trans>
                </Typography>

                <div className="flex justify-between gap-16">
                  <Typography variant="body-md">
                    <Trans>
                      Enroll to enterprise single sign-on for centralised user
                      management.
                    </Trans>
                  </Typography>

                  <Button
                    as="a"
                    to={URLS.ENTERPRISE_ENROLL}
                    variant="tertiary"
                    size="medium"
                  >
                    <span>
                      <Trans context="single sign-on">Start SSO setup</Trans>
                    </span>{' '}
                    <ArrowRight />
                  </Button>
                </div>
              </div>
            </>
          )}

          <LinkedAccounts />

          {!isLinkedToSAML && (
            <>
              <Divider />
              <div className="flex flex-col gap-24">
                <Typography variant="heading-subtle-2xl" as="h2">
                  <Trans>Delete account</Trans>
                </Typography>

                <div className="flex justify-between gap-16">
                  <Typography variant="body-md">
                    <Trans>Permanently delete your reMarkable account.</Trans>
                  </Typography>

                  <Button
                    as="a"
                    to={URLS.ACCOUNT_DELETE_LANDING}
                    variant="tertiary"
                    size="medium"
                  >
                    <span>
                      <Trans>Delete account</Trans>
                    </span>{' '}
                    <ArrowRight />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Sheet>

        {!isLinkedToSAML && (
          <div className="mt-96">
            <FaqSection
              className="my-32"
              componentLocation={ComponentLocations.ACCOUNT.SECURITY_CARD}
              sections={[
                FAQ.account.whatIsMFA(),
                FAQ.account.whatHappensIfIEnableMFA(),
              ]}
            />
          </div>
        )}
      </AppPage.Content>
    </AppPage.Container>
  );
};
